import * as GC from '@grapecity/spread-sheets';
import { clone } from '@helpers/arrays';
import { fromEvent } from 'rxjs';
import { filter, delay } from 'rxjs/operators';
import { clipboardCopy } from '@helpers/clipboard-events';
import { spreadsheetConfig } from '@shared/spreadsheet/spreadsheet-config';

const codes = {
  v: 86,
  c: 67,
  x: 88,
  rightClick: 3,
};

export class SpreadContextMenu {
  private static _allOptions: GC.Spread.Sheets.ContextMenu.IMenuItemData[] = null;

  static get allOptions(): GC.Spread.Sheets.ContextMenu.IMenuItemData[] {
    if (SpreadContextMenu._allOptions === null) {
      const contextMenu = new GC.Spread.Sheets.ContextMenu.ContextMenu();
      SpreadContextMenu._allOptions = clone(contextMenu.menuData);
    }
    return SpreadContextMenu._allOptions;
  }

  static setSimpleCopy(book: GC.Spread.Sheets.Workbook) {
    const enabledCommands = ['gc.spread.copy'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => enabledCommands.includes(menuItem.name));
    return this;
  }

  static disableHideRows(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.hideRows', 'gc.spread.unhideRows'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableHideColumns(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.hideColumns', 'gc.spread.unhideColumns'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableInsertRows(book: GC.Spread.Sheets.Workbook) {
    const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
    sheet.options.protectionOptions.allowInsertRows = false;
    const disabledCommands = ['gc.spread.insertRows'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableDeleteRows(book: GC.Spread.Sheets.Workbook) {
    const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
    sheet.options.protectionOptions.allowDeleteRows = false;

    const disabledCommands = ['gc.spread.deleteRows'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static enableDeleteRows(book: GC.Spread.Sheets.Workbook) {
    const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
    sheet.options.protectionOptions.allowDeleteRows = true;

    const option = SpreadContextMenu.allOptions.find(({ name }) => name === 'gc.spread.deleteRows');
    book.contextMenu.menuData.push({ type: 'separator' }, option);
    return this;
  }

  static enableInsertRowsAboveAndBelow(book: GC.Spread.Sheets.Workbook) {
    const insertAbove = {
      text: 'Insert Rows Above',
      name: 'gc.spread.insertRows',
      command: 'gc.spread.contextMenu.insertRows',
      workArea: 'rowHeader',
    };
    const insertBelow = {
      text: 'Insert Rows Below',
      name: 'addRowBelowCmd',
      workArea: 'rowHeader',
      command: 'addRowBelowCmd',
    };
    // const disabledCommands = ['gc.spread.insertRows'];
    // book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    book.contextMenu.menuData.push({ type: 'separator' }, insertAbove, insertBelow);
    return this;
  }

  static disableInsertColumns(book: GC.Spread.Sheets.Workbook) {
    const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
    sheet.options.protectionOptions.allowInsertColumns = false;
    const disabledCommands = ['gc.spread.insertColumns'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableDeleteColumns(book: GC.Spread.Sheets.Workbook) {
    const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
    sheet.options.protectionOptions.allowDeleteColumns = false;
    const disabledCommands = ['gc.spread.deleteColumns'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableClearContent(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.clearContents'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableInsertComments(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.insertComment'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableFilter(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.filter'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableSort(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.sort'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableInsertComment(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.insertComment'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static setCopyCutPaste(book: GC.Spread.Sheets.Workbook, parentSelector?: string) {
    // Clean the context menu to allow copy, cut, paste only
    const allowedPasteCommand = 'gc.spread.pasteValues';
    const allowedCommands = ['gc.spread.copy', 'gc.spread.cut', allowedPasteCommand];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => allowedCommands.includes(menuItem.name));

    const pasteOption = book.contextMenu.menuData.find(c => c.name === allowedPasteCommand);
    if (pasteOption) {
      delete pasteOption.group;

      // ! Disabling Right-click Paste for the beta
      // pasteOption.text = 'Paste';
      pasteOption.text = 'Paste (Use Ctrl + V instead)';
      pasteOption.disable = true;
    }

    SpreadContextMenu.enableCustomClipboardActions(book, parentSelector).focusOnRightClickOutsideSelection(book);

    return this;
  }

  static enableCustomClipboardActions(
    book: GC.Spread.Sheets.Workbook,
    // ! Fix 6255: ParentSelector used to apply fix 6255 to result pages
    parentSelector?: string
  ) {
    // Customize the events for keyboard and right-click entry points

    // ! Order warning:
    // The click actions will use the position in the context menu.
    // Being first for copy, second for cut, and third for paste itmes in the whole context menu
    const keyEventNames: string[] = ['keydown', 'keypress', 'keyup'];

    // ! Fix 6255
    const matches = document.getElementsByClassName(parentSelector || spreadsheetConfig.containerParentId);
    const container = matches && matches[matches.length - 1]; // Selecting the last item

    // We need to attach the event to the panel main body because the spread-container is
    // generated dynamically and wait until the contextMenu has been created in the DOM
    // After the right click and delay, the context menu becomes available in the DOM
    const rightClick$ = fromEvent(container, 'mouseup').pipe(
      filter((e: MouseEvent) => e['which'] === codes.rightClick),
      delay(50)
    );
    rightClick$.subscribe((rightClickEvent: MouseEvent) => {
      // console.log('executed once the spreadsheet is right cliked', rightClickEvent);

      // When the right click is executed that cell is selected if the cell is outside the current selection
      const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
      const selection = sheet.getSelections()[0];
      // ? Improve it using all the selections array if is required

      const actions = {
        copy: 'gc-spread-copy',
        cut: 'gc-spread-cut',
        paste: 'gc-spread-paste',
      };
      const actionPerformed = (element: HTMLElement) => {
        const action = element.innerHTML.includes(actions.copy)
          ? actions.copy
          : element.innerHTML.includes(actions.cut)
          ? actions.cut
          : element.innerHTML.includes(actions.paste)
          ? actions.paste
          : null;
        return action;
      };

      function clickListener(event: MouseEvent): void {
        // helper to detect clicks on the icon, the blank space or the text
        const eventType = event.target['parentNode'].innerText.toLowerCase();

        const copyAction = (_sheet, position) => {
          // Custom logic for copying
          // ------------------------
          // Build the content to be copied
          let content = '';

          // Avoid copying blank cells because of the headers
          for (let r = position.row.start; r <= position.row.end; r++) {
            for (let c = position.col.start; c <= position.col.end; c++) {
              const viewport = GC.Spread.Sheets.SheetArea.viewport;

              const val = _sheet.getCell(r, c, viewport).value();
              if (r > -1 && c > -1) {
                content += val === null ? '' : val;
              }

              // Add column separators
              if (c > -1 && position.col.end - position.col.start > 0 && c < position.col.end) {
                content += spreadsheetConfig.excelTextSeparators.col;
              }
            }
            // Add row separators
            if (r > -1 && position.row.end - position.row.start > 0 && r < position.row.end) {
              content += spreadsheetConfig.excelTextSeparators.row;
            }
          }

          // Push content to laptop's clipboard
          clipboardCopy(content);
          // console.log('Text copied to clipboard:', content);
        };

        if (Object.keys(actions).includes(eventType)) {
          let position = {
            row: {
              start: selection.row,
              end: selection.row + selection.rowCount - 1,
            },
            col: {
              start: selection.col,
              end: selection.col + selection.colCount - 1,
            },
          };
          let eventCommand: object = {
            ctrlKey: true,
            bubbles: true,
          };

          switch (eventType) {
            // COPY
            // ----
            case 'copy':
              // console.log('copying');

              // ! preventDefault required:
              // -- If we do not prevent it, spreadJS uses his own copy built-in functionality
              event.preventDefault();

              copyAction(sheet, position);

              // Trigger the event
              eventCommand = {
                ...eventCommand,
                key: 'c',
                code: 'KeyC',
                which: codes.c,
                keyCode: codes.c,
              };
              keyEventNames.forEach((_keyEventName: string) => {
                eventCommand['type'] = _keyEventName;
                container.dispatchEvent(new KeyboardEvent(_keyEventName, eventCommand));
              });
              // book.commandManager().execute({ cmd: 'copy' });
              // document.execCommand('copy');
              break;

            // CUT
            // ----
            case 'cut':
              // console.log('cuting');
              // 1. Copy
              copyAction(sheet, position);

              // 2. If we don't use prevent Default, the built-in functionality deletes the cell's content already

              // Trigger the event
              eventCommand = {
                ...eventCommand,
                key: 'x',
                code: 'KeyX',
                which: codes.x,
                keyCode: codes.x,
              };
              keyEventNames.forEach((_keyEventName: string) => {
                eventCommand['type'] = _keyEventName;
                container.dispatchEvent(new KeyboardEvent(_keyEventName, eventCommand));

                // 3. Add the next lines at the start of the SpreadJS ClipboardPasting event
                // to delete the clipboard content once the events end (clear the laptop's clipboard):
                // -------------------------------------------------------------------------------------------
                // Clear the clipboard content once the events ends
                // clipboardClearOnCut(info, this.sheet);
              });
              // book.commandManager().execute({ cmd: 'cut' });
              // document.execCommand('cut');
              break;

            // PASTE
            // -----
            // case 'paste':
            // ! Right-click Paste disabled for Beta as per fix 3185
            //   // console.log('pasting');
            //   getClipboardData()
            //     .then(pastedText => paste(sheet, selection, pastedText /* ***, event*/))
            //     .then(done => {
            //       if (done) {
            //         // Trigger the event
            //         eventCommand = {
            //           ...eventCommand,
            //           key: 'v',
            //           code: 'KeyV',
            //           which: codes.v,
            //           keyCode: codes.v
            //         };
            //         keyEventNames.forEach((_keyEventName: string) => {
            //           eventCommand['type'] = _keyEventName;
            //           container.dispatchEvent(new KeyboardEvent(_keyEventName, eventCommand));
            //         });
            //         // book.commandManager().execute({ cmd: 'paste' });
            //         // document.execCommand('paste');
            //       }
            //     })
            //     .catch(err => {
            //       console.warn(
            //         // tslint:disable-next-line:max-line-length
            //         `Error right-click pasting from the clipboard. The user could have block the permission to access to the clipboard content`,
            //         err
            //       );
            //       // displayClipboardReadBlockDialog();
            //     });

            //   break;
          }
        }

        // Remove eventlistener once is terminated
        container.removeEventListener('click', clickListener);
      }

      // Get the menu items and attach a click event for the actions we want to customize
      const contextMenuItems = document.querySelectorAll('.gc-ui-contextmenu-container>.gc-ui-contextmenu-menuitem');
      contextMenuItems.forEach((elem: HTMLElement) => {
        if (Object.values(actions).includes(actionPerformed(elem))) {
          // Attach the event listener to the container and not to other element
          container.addEventListener('click', clickListener);
        }
      });
    });
    return this;
  }

  static enableSimplePaste(book: GC.Spread.Sheets.Workbook) {
    const allowedPasteCommand = 'gc.spread.pasteValues';
    const disabledCommands = ['gc.spread.pasteOptions', 'gc.spread.pasteAll', 'gc.spread.pasteFormula', 'gc.spread.pasteFormatting'];

    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));

    const pasteOption = book.contextMenu.menuData.find(c => c.name === allowedPasteCommand);
    if (pasteOption) {
      delete pasteOption.group;
      pasteOption.text = 'Paste';
    }
    return this;
  }

  static focusOnRightClickOutsideSelection(book: GC.Spread.Sheets.Workbook) {
    const container = document.getElementById(spreadsheetConfig.containerParentId);
    const rightClick$ = fromEvent(container, 'mouseup');
    rightClick$
      .pipe(
        // TODO: rightClick$ Unsubscription with takeUntil pipe
        filter((e: MouseEvent) => e.which === codes.rightClick),
        delay(50)
      )
      .subscribe((rightClickEvent: MouseEvent) => {
        const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
        const selection = sheet.getSelections()[0];
        // TODO: Use all the selections array
        /* TODO: Deprecated: MouseEvent no longer has the layerX and layerY properties. */
        const hitTest = sheet.hitTest((rightClickEvent as any).layerX, (rightClickEvent as any).layerY);
        const clickIsInsideSelection =
          hitTest.hitTestType === 3 &&
          hitTest.row >= selection.row &&
          hitTest.col >= selection.col &&
          hitTest.row < selection.row + selection.rowCount &&
          hitTest.col < selection.col + selection.colCount;

        if (!clickIsInsideSelection && hitTest.hitTestType === 3) {
          // User has clicked in a cell. Put the focus on it
          sheet.setSelection(hitTest.row, hitTest.col, 1, 1);
        }
      });

    return this;
  }
}
